import { useState } from "react";
import { Clock, CheckCircle, XCircle, Pencil, Copy, Check } from "lucide-react";
import Markdown from "react-markdown";
import { type AffiliateRecord, resolvePhotoUrl } from "@/lib/affiliate";
import { getPlatformByKey } from "@/lib/social-utils";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";

interface Props {
  affiliate: AffiliateRecord;
  onEdit: () => void;
}

const statusConfig = {
  pending: { label: "Pending Review", icon: Clock, className: "text-yellow-500 bg-yellow-500/10" },
  approved: { label: "Approved", icon: CheckCircle, className: "text-green-500 bg-green-500/10" },
  rejected: { label: "Not Approved", icon: XCircle, className: "text-destructive bg-destructive/10" },
} as const;

export function AffiliateProfileCard({ affiliate, onEdit }: Props) {
  const [copied, setCopied] = useState(false);
  const shareUrl = affiliate.shareLink ?? (affiliate.referralCode ? `https://n8nvibes.com/ref/${affiliate.referralCode}` : null);
  const initials = affiliate.name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const photoSrc = resolvePhotoUrl(affiliate.photoUrl);
  const status = statusConfig[affiliate.status as keyof typeof statusConfig] ?? statusConfig.pending;
  const StatusIcon = status.icon;

  const socialEntries: { key: string; value: string }[] = [
    { key: "instagram", value: affiliate.socialInstagram },
    { key: "youtube", value: affiliate.socialYoutube },
    { key: "tiktok", value: affiliate.socialTiktok },
    { key: "linkedin", value: affiliate.socialLinkedin },
    { key: "twitter", value: affiliate.socialTwitter },
    { key: "website", value: affiliate.socialWebsite },
  ].filter((s): s is { key: string; value: string } => !!s.value);

  const joinedDate = new Date(affiliate.createdAt).toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
  });

  return (
    <div className="flex flex-col gap-5">
      {/* Avatar + Name */}
      <div className="flex flex-col items-center gap-3">
        <Avatar className="h-[180px] w-[180px] border-4 border-background">
          {photoSrc && <AvatarImage src={photoSrc} alt={affiliate.name} />}
          <AvatarFallback className="text-5xl bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white">{initials}</AvatarFallback>
        </Avatar>
        <h2 className="text-lg font-semibold leading-tight text-center">{affiliate.name}</h2>
        {affiliate.bio && (
          <div className="text-sm text-muted-foreground text-center prose prose-sm prose-invert max-w-none [&_a]:text-primary [&_a]:underline">
            <Markdown allowedElements={["p", "strong", "em", "a"]} unwrapDisallowed>
              {affiliate.bio}
            </Markdown>
          </div>
        )}
      </div>

      {/* Status badge */}
      <div className="flex justify-center">
        <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${status.className}`}>
          <StatusIcon className="size-3.5" />
          {status.label}
        </span>
      </div>

      <Separator />

      {/* Joined date */}
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Clock className="size-4 shrink-0" />
        <span>Joined {joinedDate}</span>
      </div>

      {/* Socials with brand icons */}
      {socialEntries.length > 0 && (
        <>
          <Separator />
          <div className="flex flex-col gap-2">
            {socialEntries.map((entry) => {
              const platform = getPlatformByKey(entry.key);
              if (!platform) return null;
              const Icon = platform.icon;
              const handle = platform.extractHandle(entry.value);
              const isUrl = entry.value.startsWith("http");
              return (
                <div key={entry.key} className="flex items-center gap-2">
                  <Icon className="size-4 shrink-0 text-muted-foreground" />
                  {isUrl ? (
                    <a
                      href={entry.value}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-muted-foreground hover:text-foreground transition-colors truncate"
                    >
                      {handle}
                    </a>
                  ) : (
                    <span className="text-sm text-muted-foreground truncate">{entry.value}</span>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      <Separator />

      <Button variant="outline" size="sm" className="w-full" onClick={onEdit}>
        <Pencil className="size-3.5 mr-1.5" />
        Manage Account
      </Button>

      {affiliate.status === "approved" && shareUrl && (
        <>
          <Separator />
          <div className="flex flex-col gap-2">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Referral Link</p>
            <div className="flex items-center gap-1.5">
              <p className="text-xs text-muted-foreground truncate flex-1 font-mono">{shareUrl}</p>
              <button
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(shareUrl);
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                  } catch {}
                }}
                className="shrink-0 p-1 rounded hover:bg-muted transition-colors"
              >
                {copied ? <Check className="size-3.5 text-green-500" /> : <Copy className="size-3.5 text-muted-foreground" />}
              </button>
            </div>
            {affiliate.referralCode && (
              <p className="text-xs text-muted-foreground">
                Code: <span className="font-mono font-medium text-foreground">{affiliate.referralCode}</span>
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
}
